import { getValidShareId } from "../cards/shareAttribution";

export const PENDING_AUTH_INTENT_STORAGE_KEY = "lettucetalk.pendingAuthIntent";
export const PENDING_AUTH_INTENT_TTL_MS = 30 * 60 * 1000;

export const PENDING_AUTH_INTENT_ACTIONS = Object.freeze({
  CREATE_ANOTHER: "create-another",
  KEEP_CARDS: "keep-cards",
});

const SUPPORTED_ACTIONS = new Set(Object.values(PENDING_AUTH_INTENT_ACTIONS));

const STRING_LIMITS = Object.freeze({
  cardId: 200,
  generationId: 200,
  returnTo: 1000,
  partner: 100,
  rawThought: 4000,
  translationLanguage: 100,
});

function getStorage() {
  try {
    if (typeof window === "undefined") {
      return null;
    }

    return window.sessionStorage || null;
  } catch (error) {
    return null;
  }
}

function sanitizeString(value, maxLength) {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return undefined;
  }

  return trimmedValue.slice(0, maxLength);
}

function sanitizeReturnTo(value) {
  const returnTo = sanitizeString(value, STRING_LIMITS.returnTo);

  if (!returnTo || !returnTo.startsWith("/") || returnTo.startsWith("//")) {
    return undefined;
  }

  return returnTo;
}

function sanitizeDraft(draft) {
  if (!draft || typeof draft !== "object" || Array.isArray(draft)) {
    return undefined;
  }

  const sanitizedDraft = {};
  const rawThought = sanitizeString(draft.rawThought, STRING_LIMITS.rawThought);
  const translationLanguage = sanitizeString(
    draft.translationLanguage,
    STRING_LIMITS.translationLanguage
  );

  if (rawThought) {
    sanitizedDraft.rawThought = rawThought;
  }

  if (translationLanguage) {
    sanitizedDraft.translationLanguage = translationLanguage;
  }

  return Object.keys(sanitizedDraft).length > 0 ? sanitizedDraft : undefined;
}

function sanitizeCreatedAt(value, fallbackCreatedAt) {
  if (Number.isFinite(value) && value > 0) {
    return value;
  }

  return fallbackCreatedAt;
}

function sanitizePendingAuthIntent(intent, fallbackCreatedAt) {
  if (!intent || typeof intent !== "object" || Array.isArray(intent)) {
    return null;
  }

  const action = sanitizeString(intent.action, 100);

  if (!SUPPORTED_ACTIONS.has(action)) {
    return null;
  }

  const createdAt = sanitizeCreatedAt(intent.createdAt, fallbackCreatedAt);

  if (!Number.isFinite(createdAt)) {
    return null;
  }

  const sanitizedIntent = {
    action,
    createdAt,
  };
  const cardId = sanitizeString(intent.cardId, STRING_LIMITS.cardId);
  const generationId = sanitizeString(intent.generationId, STRING_LIMITS.generationId);
  const returnTo = sanitizeReturnTo(intent.returnTo);
  const partner = sanitizeString(intent.partner, STRING_LIMITS.partner);
  const sourceShareId = getValidShareId(intent.sourceShareId);
  const draft = sanitizeDraft(intent.draft);

  if (cardId) {
    sanitizedIntent.cardId = cardId;
  }

  if (generationId) {
    sanitizedIntent.generationId = generationId;
  }

  if (returnTo) {
    sanitizedIntent.returnTo = returnTo;
  }

  if (partner) {
    sanitizedIntent.partner = partner;
  }

  if (sourceShareId) {
    sanitizedIntent.sourceShareId = sourceShareId;
  }

  if (draft) {
    sanitizedIntent.draft = draft;
  }

  return sanitizedIntent;
}

export function isPendingAuthIntentExpired(
  intent,
  now = Date.now(),
  ttlMs = PENDING_AUTH_INTENT_TTL_MS
) {
  if (!intent || !Number.isFinite(intent.createdAt)) {
    return true;
  }

  return now - intent.createdAt > ttlMs;
}

export function savePendingAuthIntent(intent) {
  const storage = getStorage();

  if (!storage) {
    return null;
  }

  const sanitizedIntent = sanitizePendingAuthIntent(intent, Date.now());

  if (!sanitizedIntent) {
    return null;
  }

  try {
    storage.setItem(
      PENDING_AUTH_INTENT_STORAGE_KEY,
      JSON.stringify(sanitizedIntent)
    );
    return sanitizedIntent;
  } catch (error) {
    return null;
  }
}

export function clearPendingAuthIntent() {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  try {
    storage.removeItem(PENDING_AUTH_INTENT_STORAGE_KEY);
  } catch (error) {
    // Storage can fail in privacy modes; callers should not need special handling.
  }
}

export function getPendingAuthIntent() {
  const storage = getStorage();

  if (!storage) {
    return null;
  }

  let storedIntent;

  try {
    storedIntent = storage.getItem(PENDING_AUTH_INTENT_STORAGE_KEY);
  } catch (error) {
    return null;
  }

  if (!storedIntent) {
    return null;
  }

  try {
    const parsedIntent = JSON.parse(storedIntent);
    const sanitizedIntent = sanitizePendingAuthIntent(parsedIntent);

    if (!sanitizedIntent || isPendingAuthIntentExpired(sanitizedIntent)) {
      clearPendingAuthIntent();
      return null;
    }

    return sanitizedIntent;
  } catch (error) {
    clearPendingAuthIntent();
    return null;
  }
}

export function consumePendingAuthIntent() {
  const intent = getPendingAuthIntent();

  clearPendingAuthIntent();

  return intent;
}
